/**
 * VerbRail — sticky mini nav that highlights the current section as visitors
 * scroll through the Landing page narrative. Uses IntersectionObserver so
 * scroll performance stays smooth.
 *
 * The rail sits below the top header and above the fold, glass-frosted, and
 * lights up the active verb as its section enters the viewport.
 */
import { useEffect, useState } from "react";

const VERBS = [
  { verb: "Know", anchor: "#features" },
  { verb: "Understand", anchor: "#ai" },
  { verb: "Forecast", anchor: "#forecast" },
  { verb: "Decide", anchor: "#decide" },
  { verb: "Act", anchor: "#act" },
];

export default function VerbRail() {
  const [active, setActive] = useState(null);
  const [visible, setVisible] = useState(false);

  // Show once user has scrolled past the initial narrative section.
  useEffect(() => {
    const onScroll = () => {
      const narrative = document.querySelector("#narrative");
      if (!narrative) return;
      const rect = narrative.getBoundingClientRect();
      setVisible(rect.bottom < 60);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which anchored section is most-visible.
  useEffect(() => {
    const targets = VERBS.map((v) => document.querySelector(v.anchor)).filter(Boolean);
    if (targets.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (inView.length > 0) {
          const hit = VERBS.find((v) => `#${inView[0].target.id}` === v.anchor);
          if (hit) setActive(hit.verb);
        }
      },
      { rootMargin: "-30% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  const jump = (e, anchor) => {
    e.preventDefault();
    const el = document.querySelector(anchor);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("verb-target-glow");
      setTimeout(() => el.classList.remove("verb-target-glow"), 1400);
    }
  };

  return (
    <div
      data-testid="verb-rail"
      className={`sticky top-0 z-40 border-b border-border/60 bg-white/85 backdrop-blur-md transition-all ${
        visible ? "opacity-100 translate-y-0" : "pointer-events-none -translate-y-4 opacity-0"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-6 py-2 text-xs md:px-12">
        <span className="mr-2 shrink-0 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
          Story
        </span>
        {VERBS.map((v, i) => {
          const isActive = active === v.verb;
          return (
            <a
              key={v.verb}
              href={v.anchor}
              onClick={(e) => jump(e, v.anchor)}
              data-testid={`verb-rail-${v.verb.toLowerCase()}`}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors ${
                isActive
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-accent"
              }`}
            >
              <span className="font-mono text-[10px] opacity-60">0{i + 1}</span>
              <span className="font-medium">{v.verb}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}
